import React, { useMemo, useState } from 'react';
import { Table, Pagination } from 'antd';
import type { TableProps } from 'antd';
import { useIsMobile } from '../../hooks/useIsMobile';
import './responsiveTable.css';

/**
 * Замена <Table> для страниц товаров.
 *
 * На компьютере это обычная таблица Ant Design — ни одного изменения.
 * На телефоне таблица заменяется списком карточек.
 *
 * Устройство карточки. Сверху то, по чему товар опознают: бренд, артикул,
 * номер детали. Затем остаток и цена крупно — именно их ищут глазами при
 * пролистывании. Остальные характеристики (у свечей их тринадцать) убраны
 * под раскрытие: развёрнутая карточка занимала целый экран, а товаров
 * в разделе три с половиной сотни.
 *
 * Карточки строятся ИЗ ТЕХ ЖЕ колонок, что и таблица: заголовок колонки
 * становится подписью, её render — значением. Поэтому виды не расходятся:
 * добавили колонку в таблицу — она сама появилась в карточке.
 */

// Ключи колонок одинаковы во всех пяти таблицах товаров.
const ACTION_KEYS = ['edit', 'delete'];          // в подвал карточки
const HEAD_KEYS = ['brand', 'model'];            // крупная шапка
const SUB_KEYS = ['partNumber'];                 // под шапкой, мелко
const BADGE_KEYS = ['original'];                 // метка «Оригинал/Копия»
const SUMMARY_KEYS = ['quantity', 'price'];      // остаток и цена, крупно

type AnyCol = {
    key?: React.Key;
    dataIndex?: string | string[];
    title?: unknown;
    render?: (value: unknown, record: unknown, index: number) => React.ReactNode;
};

const colKey = (col: AnyCol, i: number) =>
    String(col.key ?? (Array.isArray(col.dataIndex) ? col.dataIndex.join('.') : col.dataIndex) ?? i);

const readValue = (record: any, dataIndex?: string | string[]) => {
    if (dataIndex === undefined) return undefined;
    if (Array.isArray(dataIndex)) return dataIndex.reduce((acc, k) => acc?.[k], record);
    return record?.[dataIndex];
};

const cellContent = (col: AnyCol, record: any, index: number): React.ReactNode => {
    const raw = readValue(record, col.dataIndex);
    if (col.render) return col.render(raw, record, index) as React.ReactNode;
    return raw as React.ReactNode;
};

// Пустое значение не занимает место: прочерков в этих таблицах много
// (та же колонка SKU), а на телефоне каждая строка на счету.
const isBlank = (v: React.ReactNode) =>
    v === null || v === undefined || v === '' || v === '—' || v === '-';

function ResponsiveTable<T extends object>(props: TableProps<T>) {
    const isMobile = useIsMobile();
    const [openCards, setOpenCards] = useState<Set<string>>(new Set());

    const columns = (props.columns ?? []) as AnyCol[];
    const data = (props.dataSource ?? []) as any[];

    const groups = useMemo(() => {
        const pick = (keys: string[]) => columns.filter((c, i) => keys.includes(colKey(c, i)));
        const taken = [...ACTION_KEYS, ...HEAD_KEYS, ...SUB_KEYS, ...BADGE_KEYS, ...SUMMARY_KEYS];
        let head = pick(HEAD_KEYS);
        if (head.length === 0) head = columns.slice(0, 2);
        return {
            head,
            sub: pick(SUB_KEYS),
            badge: pick(BADGE_KEYS),
            summary: pick(SUMMARY_KEYS),
            details: columns.filter((c, i) => !taken.includes(colKey(c, i)) && !head.includes(c)),
            actions: pick(ACTION_KEYS),
        };
    }, [columns]);

    if (!isMobile) return <Table<T> {...props} />;

    const pag: any = props.pagination;
    const current = pag && pag.current ? pag.current : 1;
    const pageSize = pag && pag.pageSize ? pag.pageSize : data.length || 10;
    const pageData = pag === false ? data : data.slice((current - 1) * pageSize, current * pageSize);

    const keyOf = (record: any, index: number) => {
        const rk = props.rowKey;
        if (typeof rk === 'function') return String(rk(record, index));
        if (typeof rk === 'string') return String(record[rk]);
        return String(index);
    };

    const toggle = (id: string) => setOpenCards((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
    });

    return (
        <div className="rt-cards">
            {pageData.length === 0 && <div className="rt-empty">Ничего не найдено</div>}

            {pageData.map((record, index) => {
                const id = keyOf(record, index);
                const open = openCards.has(id);
                const extra = props.rowClassName
                    ? (props.rowClassName as (r: any, i: number, ind: number) => string)(record, index, 0)
                    : '';
                const rowProps = props.onRow ? props.onRow(record, index) : {};

                const details = groups.details
                    .map((col, i) => ({ col, i, value: cellContent(col, record, index) }))
                    .filter((entry) => !isBlank(entry.value));

                return (
                    <div key={id} className={`rt-card ${extra}`} onDoubleClick={rowProps.onDoubleClick as any}>
                        <div className="rt-card__head">
                            {groups.head.map((col, i) => (
                                <span key={colKey(col, i)} className="rt-card__head-part">
                                    {cellContent(col, record, index)}
                                </span>
                            ))}
                        </div>

                        {(groups.sub.length > 0 || groups.badge.length > 0) && (
                            <div className="rt-card__sub">
                                <span>
                                    {groups.sub.map((col, i) => (
                                        <React.Fragment key={colKey(col, i)}>
                                            {cellContent(col, record, index)}
                                        </React.Fragment>
                                    ))}
                                </span>
                                <span className="rt-card__badge">
                                    {groups.badge.map((col, i) => (
                                        <React.Fragment key={colKey(col, i)}>
                                            {cellContent(col, record, index)}
                                        </React.Fragment>
                                    ))}
                                </span>
                            </div>
                        )}

                        {groups.summary.length > 0 && (
                            <div className="rt-card__summary">
                                {groups.summary.map((col, i) => (
                                    <div key={colKey(col, i)} className="rt-card__summary-item">
                                        <span className="rt-card__summary-label">{col.title as React.ReactNode}</span>
                                        <span className="rt-card__summary-value">{cellContent(col, record, index)}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {details.length > 0 && (
                            <>
                                <button type="button" className="rt-card__toggle" onClick={() => toggle(id)}>
                                    Характеристики
                                    <span className={`rt-card__chev ${open ? 'is-open' : ''}`} aria-hidden="true">▾</span>
                                </button>
                                {open && (
                                    <dl className="rt-card__body">
                                        {details.map((entry) => (
                                            <React.Fragment key={colKey(entry.col, entry.i)}>
                                                <dt>{entry.col.title as React.ReactNode}</dt>
                                                <dd>{entry.value}</dd>
                                            </React.Fragment>
                                        ))}
                                    </dl>
                                )}
                            </>
                        )}

                        {groups.actions.length > 0 && (
                            <div className="rt-card__actions">
                                {groups.actions.map((col, i) => (
                                    <span key={colKey(col, i)}>{cellContent(col, record, index)}</span>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}

            {pag !== false && data.length > pageSize && (
                <div className="rt-cards__pager">
                    <Pagination
                        current={current}
                        pageSize={pageSize}
                        total={pag && pag.total ? pag.total : data.length}
                        showSizeChanger={pag ? pag.showSizeChanger : false}
                        pageSizeOptions={pag ? pag.pageSizeOptions : undefined}
                        onChange={pag ? pag.onChange : undefined}
                        onShowSizeChange={pag ? pag.onShowSizeChange : undefined}
                        size="small"
                        showLessItems
                    />
                </div>
            )}
        </div>
    );
}

export default ResponsiveTable;
